<template>
  <div class="main-wrapper">
    <verify-payment-popup v-if="showRedirectVerifyPopup" :key="refreshVerifyPopup" />
    <div class="logo" @click="pushToDashboard">
      <img :src="require('@/assets/logo.svg')" @click="pushToDashboard" />
    </div>
    <div class="close" @click="getBacked">
      <img src="@/assets/ico/close_48px.svg" />
    </div>
    <div class="main-column">
      <div v-if="!cardLast4" class="header">
        <h1>{{ $t('upgradeTeam') }}</h1>
        <h5>{{ $t('addPaymentMethodAndReviewUpgrade') }}</h5>
      </div>
      <div v-else class="header">
        <h1>{{ currentPaidPlan ? (isMonthly ? $t('switchToMonthlyBilling') : $t('switchToAnnualBilling')) : $t('upgradeTeam') }}</h1>
        <h5 v-if="!isMonthly">
          {{ $t('payForAYearAheadAndSave') }} <span>{{ $t('saveXPercent', { x: savePercent }) }}</span>
        </h5>
        <h5 v-else />
      </div>
      <div class="two-columns" :class="{ 'switch-yearly': cardLast4 || isBillingInfo }">
        <div v-if="!cardLast4 && !isBillingInfo" class="single-column left-column">
          <section class="company-info-section">
            <h6 id="companyInfo1">
              {{ $t('companyInfo1') }}
            </h6>
            <!--            <p class="field-title">{{ $t('country') }}</p>-->
            <!--            <v-select class="style-chooser"-->
            <!--                      v-model="country"-->
            <!--                      label="name"-->
            <!--                      :disabled="disablecondition === 1"-->
            <!--                      :options="countries"-->
            <!--                      @input="countrySelected"></v-select>-->
            <dropdown
              :key="country.code"
              v-model="country.name"
              :editable="true"
              :options="countries"
              :auto-complete="true"
              :title="$t('country')"
              @focus-next-input="focusNext('companyInputRef')"
              @input="countrySelected"
            />
            <editable-input
              ref="companyInputRef"
              v-model="companyName"
              :title="businessPurchase ? $t('companyName') : $t('name')"
              reference="companyInputRef"
              :incorrect-value-msg="$t('fieldCannotBeEmpty')"
              :disabled="disablecondition === 1"
              lazy-validation
              auto-capitalize="words"
              @focus-next-input="focusNext('line1')"
            />

            <editable-input
              id="line1"
              ref="line1"
              v-model="addressLine1"
              reference="line1"
              lazy-validation
              auto-capitalize="words"
              :incorrect-value-msg="$t('fieldCannotBeEmpty')"
              :title="$t('addressLine1')"
              :disabled="disablecondition === 1"
              @focus-next-input="focusNext('line2')"
            />
            <editable-input
              id="line2"
              ref="line2"
              v-model="addressLine2"
              reference="line2"
              lazy-validation
              auto-capitalize="words"
              :placeholder="$t('phOptional')"
              :disabled="disablecondition === 1"
              @focus-next-input="focusNext('city')"
            />

            <div class="row">
              <editable-input
                id="city"
                ref="city"
                v-model="city"
                reference="city"
                lazy-validation
                auto-capitalize="words"
                :title="$t('city')"
                :incorrect-value-msg="$t('fieldCannotBeEmpty')"
                :disabled="disablecondition === 1"
                @keyup.enter="$event.target.nextElementSibling.focus()"
                @focus-next-input="focusNext('zipCode')"
              />
              <editable-input
                id="state-province"
                ref="stateProvince"
                v-model="stateProvince"
                reference="stateProvince"
                lazy-validation
                auto-capitalize="words"
                :title="$t('stateProvince')"
                :placeholder="$t('phOptional')"
                :disabled="disablecondition === 1"
                @focus-next-input="focusNext('zipCode')"
              />
            </div>

            <editable-input
              id="zip-code"
              ref="zipCode"
              v-model="zipCode"
              reference="zipCode"
              lazy-validation
              :incorrect-value-msg="$t('fieldCannotBeEmpty')"
              :disabled="disablecondition === 1"
              :title="$t('zipCode')"
              @focus-next-input="focusNext(isEU && businessPurchase ? 'vatId' : 'nameOnCard')"
            />
            <p v-if="isEU" class="radio-group-subtitle">
              {{ $t('isBusinessPurchase') }}
            </p>
            <radio-button v-if="isEU" :text="'yes'" class="radio-button" :checked="businessPurchase" @click="businessPurchaseToggled" />
            <radio-button v-if="isEU" :text="'no'" class="radio-button" :checked="!businessPurchase" @click="businessPurchaseToggled" />

            <editable-input
              v-if="isEU && businessPurchase"
              id="vat-id"
              ref="vatId"
              v-model="vatId"
              reference="vatId"
              vat-restriction
              :vat-id-string="countryCode"
              :incorrect-value-msg="vatId === '' ? this.$t('fieldCannotBeEmpty') : incorrectNipText"
              :disabled="disablecondition === 1"
              :placeholder="countryCode === 'PL' ? '9999999999' : countryCode + '9999999999'"
              :title="countryCode === 'PL' ? $t('nip') : $t('vatId')"
              :footer="countryCode === 'PL' ? '' : $t('includeYourCountryCode')"
              @focus-next-input="focusNext('nameOnCard')"
            />
          </section>
          <section class="payment-method-section">
            <section v-if="!cardLast4" class="payment-grid">
              <h6>{{ $t('paymentMethod2') }}</h6>
              <editable-input
                id="nameOnCard"
                ref="nameOnCard"
                v-model="billingDetails.name"
                :incorrect-value-msg="$t('fieldCannotBeEmpty')"
                reference="nameOnCard"
                lazy-validation
                auto-capitalize="words"
                :disabled="disablecondition === 1"
                :title="$t('nameOnCard')"
                @focus-next-input="cardNumber.focus()"
              />
              <div class="stripe-card-with-error card-number">
                <div class="stripe-card-element" :class="{ focused: focusedNum }">
                  <span class="field-title">{{ $t('cardNumber') }}</span>
                  <label :class="{ focused: focusedNum, incorrect: incorrectNumber, disabled: disablecondition === 1 }">
                    <div class="brand-cardnumber-positioning">
                      <span class="brand">
                        <transition name="slide">
                          <img v-if="brandImages[cardBrand]" :key="cardBrand" :src="brandImages[cardBrand]" />
                          <img v-else :key="cardBrand" :src="require('@/assets/_desktop/_payments/icons/cc/unknown.svg')" />
                        </transition>
                      </span>
                      <div id="cardNumber" ref="cardNumber" />
                    </div>
                  </label>
                </div>
                <div id="card-number-error" class="card-error" />
              </div>
              <div class="stripe-card-with-error  card-expiry">
                <div class="stripe-card-element" :class="{ focused: focusedExp }">
                  <span class="field-title">{{ $t('cardExpiry') }}</span>
                  <label :class="{ focused: focusedExp, incorrect: incorrectExpiry, disabled: disablecondition === 1 }">
                    <div id="cardExpiry" ref="cardExpiry" />
                  </label>
                </div>
                <div id="card-expiration-error" class="card-error" />
              </div>

              <div class="stripe-card-with-error  card-cvc">
                <div class="stripe-card-element" :class="{ focused: focusedCvv }">
                  <span class="field-title"
                    >{{ $t('cardCvc') }}
                    <div class="cvv-question-mark">
                      <img src="@/assets/_desktop/_payments/question-mark.svg" />
                      <div class="cvv-message-explanation">{{ $t('cvvExplanationMessage') }}</div>
                    </div>
                  </span>
                  <label :class="{ focused: focusedCvv, incorrect: incorrectCvv, disabled: disablecondition === 1 }">
                    <div id="cardCvc" ref="cardCvc" />
                  </label>
                </div>
                <div id="card-cvv-error" class="card-error" />
              </div>
              <div id="success" />
            </section>
            <section v-if="cardLast4">
              <h6>{{ $t('paymentMethod') }}</h6>
              <h3>{{ $t('creditCard') }}</h3>
            </section>
            <button
              :disabled="disablecondition === 1"
              :class="{ 'clavo-button loading-dots': loadingAnimation, 'pay-with-stripe': !loadingAnimation }"
              @click="pay"
            >
              {{ paybuttontext }}
            </button>
          </section>
          <section class="payment-footer">
            <p class="footer-info"><img src="@/assets/_desktop/_payments/lock-icon.svg" />{{ $t('paymentFormInfo1') }}</p>
            <p class="footer-stripe-info">{{ $t('paymentFormInfo2') }} <img src="@/assets/_desktop/_payments/stripe-logo.svg" alt="Stripe" />.</p>
          </section>
        </div>
        <div class="single-column right-column">
          <div class="first-row">
            <div class="inside-higher-row">
              <h6>{{ typeOfPlan + ' plan' }}</h6>
              <dropdown
                v-if="!cardLast4"
                :value="isMonthly ? getPlans(typeOfPlan)[1].value : getPlans(typeOfPlan)[0].value"
                :options="plans"
                :disabled="disablecondition === 1"
                @input="planSelected"
              />
              <div v-if="cardLast4">
                {{ isMonthly ? getPlans(typeOfPlan)[1].name : getPlans(typeOfPlan)[0].name }}
              </div>
              <div v-if="!loadedDetails" :style="{ margin: '24px 0 19px' }"><Skeleton height="19px" /></div>
              <div v-else class="equation">
                <span
                  >{{ pricePerPersonPerMonth }} <span> {{ duration }} </span></span
                ><span class="multip">&#215;</span><span> {{ $tc('activeUsers', activeUsers > 1 ? 1 : 0, { x: activeUsers }) }} </span>
              </div>
            </div>
            <div v-if="!loadedDetails" class="inside-lower-row">
              <div :style="{ marginBottom: '2px' }">
                <span class="left-position"><Skeleton width="120px" height="16px"/></span>
                <span class="right-position"><Skeleton width="80px" height="16px"/></span>
              </div>
              <div v-if="cardLast4" :style="{ marginBottom: '2px' }">
                <span class="left-position"><Skeleton width="180px" height="16px"/></span>
                <span class="right-position"><Skeleton width="60px" height="16px"/></span>
              </div>
              <div :style="{ marginBottom: '3px' }">
                <span class="left-position"><Skeleton width="100px" height="16px"/></span>
                <span class="right-position"><Skeleton width="70px" height="16px"/></span>
              </div>
            </div>
            <div v-else class="inside-lower-row">
              <div>
                <span class="left-position">{{ $t('subtotal') }}</span>
                <span class="right-position"> {{ subtotal }}</span>
              </div>
              <div v-if="invoiceLineUnused">
                <span class="left-position">{{ isMonthly ? $t('unusedYearlyPlan') : $t('unusedMonthlyPlan') }}</span>
                <span class="right-position"> {{ unused }}</span>
              </div>
              <div>
                <span class="left-position">{{ $t('tax') }}</span>
                <span class="right-position"> {{ tax }}</span>
              </div>
            </div>
          </div>
          <div class="second-row">
            <h6 class="left-position">
              {{ cardLast4 ? $t('dueToday') : $t('total') }}
            </h6>
            <h6 v-if="!loadedDetails" class="right-position"><Skeleton heigth="20px" /></h6>
            <h6 v-else class="right-position">{{ total }}</h6>
          </div>
        </div>
        <section
          v-if="cardLast4 || isBillingInfo"
          :class="{ ButtonTurnOffGrid: showPaymentMethodButton || (!cardLast4 && isBillingInfo) }"
          class="payment-method"
        >
          <h6>{{ $t('paymentMethod') }}</h6>
          <div v-if="!(showPaymentMethodButton || (!cardLast4 && isBillingInfo))" class="row">
            <div class="display-card">
              <div class="display-card-tittle">
                {{ $t('creditCard') }}
              </div>

              <div class="display-card-div">
                <img
                  v-if="cardBrand"
                  :key="'@/assets/_desktop/_payments/icons/cc/' + cardBrand.toLowerCase() + '.svg' + 'div'"
                  :src="require('@/assets/_desktop/_payments/icons/cc/' + cardBrand.toLowerCase() + '.svg')"
                />
                <img v-else :src="require('@/assets/_desktop/_payments/icons/cc/unknown.svg')" />
                <span>{{ cardNumberDisplay }}</span>
              </div>
            </div>
            <button class="clavo-button secondary" @click="showPaymentMethod">
              {{ $t('changeCard') }}
            </button>
          </div>

          <section class="hide" :class="{ show: showPaymentMethodButton || (!cardLast4 && isBillingInfo) }">
            <editable-input
              id="nameOnCard"
              ref="nameOnCard"
              v-model="billingDetails.name"
              reference="nameOnCard"
              :disabled="disablecondition === 1"
              :title="$t('nameOnCard')"
              :incorrect-value-msg="$t('fieldCannotBeEmpty')"
              @focus-next-input="cardNumber.focus()"
            />
            <div class="stripe-card-with-error card-number">
              <div class="stripe-card-element" :class="{ focused: focusedNum }">
                <span class="field-title">{{ $t('cardNumber') }}</span>
                <label :class="{ focused: focusedNum, incorrect: incorrectNumber, disabled: disablecondition === 1 }">
                  <div class="brand-cardnumber-positioning">
                    <span class="brand">
                      <transition name="slide">
                        <img
                          v-if="brandImages[cardBrand]"
                          :key="'@/assets/_desktop/_payments/icons/cc/' + cardBrand.toLowerCase() + '.svg'"
                          :src="require('@/assets/_desktop/_payments/icons/cc/' + cardBrand.toLowerCase() + '.svg')"
                        />
                        <img v-else :src="require('@/assets/_desktop/_payments/icons/cc/unknown.svg')" />
                      </transition>
                    </span>
                    <div id="cardNumber" ref="cardNumber" />
                  </div>
                </label>
              </div>
              <div id="card-number-error" class="card-error" />
            </div>

            <div class="stripe-card-with-error  card-expiry">
              <div class="stripe-card-element" :class="{ focused: focusedExp }">
                <span class="field-title">{{ $t('cardExpiry') }}</span>
                <label :class="{ focused: focusedExp, incorrect: incorrectExpiry, disabled: disablecondition === 1 }">
                  <div id="cardExpiry" ref="cardExpiry" />
                </label>
              </div>
              <div id="card-expiration-error" class="card-error" />
            </div>

            <div class="stripe-card-with-error  card-cvc">
              <div class="stripe-card-element" :class="{ focused: focusedCvv }">
                <span class="field-title"
                  >{{ $t('cardCvc') }}
                  <div class="cvv-question-mark">
                    <img src="@/assets/_desktop/_payments/question-mark.svg" />
                    <div class="cvv-message-explanation">
                      {{ $t('cvvExplanationMessage') }}
                      <img src="@/assets/_desktop/_payments/CVC-pic.svg" />
                    </div></div
                ></span>
                <label :class="{ focused: focusedCvv, incorrect: incorrectCvv, disabled: disablecondition === 1 }">
                  <div id="cardCvc" ref="cardCvc" />
                </label>
              </div>
              <div id="card-cvv-error" class="card-error" />
            </div>
            <span
              v-if="cardLast4"
              class="clavo-link secondary cancel-link"
              @click="
                showPaymentMethodButton = false
                alreadyShownButton = true
              "
              >{{ $t('useDefaultCard') }}</span
            >
            <!-- <div id="success"></div> -->
          </section>
          <button
            :disabled="disablecondition === 1"
            :class="{ 'clavo-button loading-dots': loadingAnimation, 'pay-with-stripe': !loadingAnimation }"
            @click="pay"
          >
            {{ paybuttontext }}
          </button>
          <section class="payment-footer">
            <p class="footer-info"><img src="@/assets/_desktop/_payments/lock-icon.svg" />{{ $t('paymentFormInfo1') }}</p>
            <p class="footer-stripe-info">{{ $t('paymentFormInfo2') }} <img src="@/assets/_desktop/_payments/stripe-logo.svg" alt="Stripe" />.</p>
          </section>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import Modal from '@/components/Modal'
// import { stripeKey, stripeOptions } from './stripeConfig.json'
import { Card, createToken } from 'vue-stripe-elements-plus'
import EventBus from '@/EventBus'
import User from '@/components/User'
import ParseFunctions from '@/components/ParseFunctions'
import Dropdown from '@/components/UI/Dropdown'
import EditableInput from '@/components/UI/EditableInput'
import RadioButton from '@/components/UI/RadioButton'
import Constants from '@/components/Constants'
import VerifyPaymentPopup from '@/components/Billing/VerifyPaymentPopup'
import { isString } from '@sentry/utils'
import { Skeleton } from 'vue-loading-skeleton'
import BillingUtils from '@/components/Billing/BillingUtils'

export default {
  name: 'PaymentPopup',
  components: {
    RadioButton,
    Dropdown,
    EditableInput,
    VerifyPaymentPopup,
    Skeleton,
  },
  mixins: [User, ParseFunctions, Constants, BillingUtils],
  data() {
    return {
      teamId: undefined,
      stripe: undefined,
      elements: undefined,
      cardNumber: undefined,
      cardExpiry: undefined,
      cardCvc: undefined,
      expiryDate: undefined,
      isTrial: false,
      cardBrand: 'unknown',
      cardLast4: undefined,
      country: { name: '', code: '' },
      subtotal: '-',
      unused: '-',
      invoiceLineUnused: undefined,
      tax: '-',
      pricePerPersonPerMonth: '-',
      savePercent: '20',
      activeUsers: '-',
      duration: '-',
      total: '-',
      countries: [],
      vatRates: [],
      addressLine1: '',
      addressLine2: '',
      city: '',
      stateProvince: '',
      zipCode: '',
      vatId: '',
      businessPurchase: true,
      countryCode: '',
      plan: 'standardMonthly',
      currentPaidPlan: undefined,
      clicked: false,
      companyName: '',
      currency: '',
      plans: this.getPlans(),
      nameOnCard: undefined,
      disablecondition: 0,
      paybuttontext: this.$t('paynow'),
      refreshIntervalId: '',
      iteration: 0,
      cardNumberDisplay: '',
      showPaymentMethodButton: false,
      paymentMethodId: null,
      typeOfPlan: this.$t('standard'),
      isMonthly: true,
      conditionLast4: true,
      focusedNum: false,
      focusedExp: false,
      focusedCvv: false,
      incorrectNumber: false,
      incorrectExpiry: false,
      incorrectCvv: false,
      loadedDetails: false,
      billingDetails: {
        name: '',
      },
      isBillingInfo: false,
      incorrectNipText: this.$t('fieldCannotBeEmpty'),
      alreadyShownButton: false,
      showRedirectVerifyPopup: false,
      redirectUrl: '',
      refreshVerifyPopup: false,
      brandImageUrl: '@/assets/_desktop/_payments/icons/cc/unknown.svg',
      brandImages: {
        unknown: require('@/assets/_desktop/_payments/icons/cc/unknown.svg'),
        visa: require('@/assets/_desktop/_payments/icons/cc/visa.svg'),
        amex: require('@/assets/_desktop/_payments/icons/cc/amex.svg'),
        discover: require('@/assets/_desktop/_payments/icons/cc/discover.svg'),
        mastercard: require('@/assets/_desktop/_payments/icons/cc/mastercard.svg'),
        jcb: require('@/assets/_desktop/_payments/icons/cc/jcb.svg'),
      },
      loadingAnimation: false,
    }
  },
  computed: {
    isEU() {
      for (let o of this.vatRates) {
        if (o.name === this.country.name) return true
      }
      return false
    },
    brandComputed(card) {
      return '@/assets/_desktop/_payments/icons/cc/' + card + '.svg'
    },
  },
  async mounted() {
    this.mountStripeScript()
  },
  async created() {
    this.isBillingInfo = this.$route.query['isBillingInfo']
    this.isMonthly = this.$route.query['isMonthly'] === 'true'
    this.teamId = this.$route.query['teamId']
    this.plan = this.$route.query['plan']
    this.currentPaidPlan = this.$route.query['plan']
    this.isTrial = this.$route.query['isTrial']
    this.cardBrand = this.$route.query['cardBrand']
    this.cardLast4 = this.$route.query['cardLast4']
    this.paymentMethodId = this.$route.query['paymentId']
    this.countryCode = this.$route.query['country']
    this.currency = this.$route.query['currency']
    if (isString(this.isBillingInfo)) {
      this.isBillingInfo = this.isBillingInfo === 'true'
      this.isTrial = this.isTrial === 'true'
    }
    this.businessPurchase = this.$route.query['businessPurchase'] ? this.$route.query['businessPurchase'] : true
    if (this.cardLast4) {
      this.cardNumberDisplay = ' **** **** **** ' + this.cardLast4
      this.isMonthly = this.plan !== this.getPlans(this.typeOfPlan)[1].stripeName
    }
    try {
      if (!this.paymentMethodId && !this.isBillingInfo) {
        let res = await this.getCountries()

        if (res.status === 'ok') {
          for (let key in res.countries) {
            this.countries.push({ code: key, name: res.countries[key].name })
          }
        }
        res = await this.getVatRates()
        // console.log('getVatRates res', res)
        if (res.status === 'ok') {
          for (let key in res.taxRates) {
            let name = this.getNameFromCode(key)
            this.vatRates.push({ code: key, rate: res.taxRates[key], name: name })
          }
        }
        if (this.countryCode !== '') {
          this.country.name = this.getNameFromCode(this.countryCode)
          this.country.code = this.countryCode
        }
        await this.countrySelected()
        // console.log('getPlans',this.getPlans(this.typeOfPlan))
      } else {
        this.country.code = this.countryCode
      }
      this.plan = this.isMonthly ? this.getPlans(this.typeOfPlan)[1].stripeName : this.getPlans(this.typeOfPlan)[0].stripeName
      await this.updateSubscriptionPriceDetails()
      this.loadedDetails = true
      this.duration = this.plan === this.getPlans(this.typeOfPlan)[1].stripeName ? this.$t('perMonth') : this.$t('perYear')
    } catch (e) {
      console.error('created error', e)
    }

    if (!this.cardLast4) {
      await this.stripeCard()
    }
  },
  methods: {
    async stripeCard() {
      try {
        let style = {
          base: {
            color: '#081014',
            iconColor: '#081014',
            fontFamily: '"averta-regular",  sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '14px',
            letterSpacing: '0.2px',
            '::placeholder': {
              color: '#9093C0',
            },
            ':disabled': {
              color: '#9093C0',
            },
          },
          invalid: {
            color: 'black',
            iconColor: 'black',
          },
        }
        this.stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
        this.elements = this.stripe.elements()

        let cardNum = (this.cardNumber = this.elements.create('cardNumber', { style: style }))
        this.cardNumber.mount('#cardNumber')

        let cardExp = (this.cardExpiry = this.elements.create('cardExpiry', { style: style }))
        this.cardExpiry.mount('#cardExpiry')

        let cardCvv = (this.cardCvc = this.elements.create('cardCvc', { style: style }))
        this.cardCvc.mount('#cardCvc')

        cardExp.on(
          'change',
          function(event) {
            let displayError = document.getElementById('card-expiration-error')
            if (event.error) {
              this.incorrectExpiry = true
              this.focusedExp = false
              displayError.textContent = event.error.message
            } else {
              displayError.textContent = ''
              this.incorrectExpiry = false
              if (event.complete) {
                this.cardCvc.focus()
                this.focusedExp = false
              } else {
                this.focusedExp = true
              }
            }
          }.bind(this),
        )
        cardCvv.on(
          'change',
          function(event) {
            let displayError = document.getElementById('card-cvv-error')
            if (event.error) {
              this.incorrectCvv = true
              this.focusedCvv = false
              displayError.textContent = event.error.message
            } else {
              this.incorrectCvv = false
              this.focusedCvv = true
              displayError.textContent = ''
            }
          }.bind(this),
        )

        cardNum.on(
          'focus',
          function(event) {
            this.focusedNum = true
          }.bind(this),
        )
        cardNum.on(
          'blur',
          function(event) {
            this.focusedNum = false
          }.bind(this),
        )

        cardExp.on(
          'focus',
          function(event) {
            this.focusedExp = true
          }.bind(this),
        )
        cardExp.on(
          'blur',
          function(event) {
            this.focusedExp = false
          }.bind(this),
        )

        cardCvv.on(
          'focus',
          function(event) {
            this.focusedCvv = true
          }.bind(this),
        )
        cardCvv.on(
          'blur',
          function(event) {
            this.focusedCvv = false
          }.bind(this),
        )

        // function setOutcome(result) {
        //   let successElement = document.querySelector('.success');
        //   let errorElement = document.querySelector('.card-number-error');
        //   successElement.classList.remove('visible');
        //   errorElement.classList.remove('visible');
        //
        //   if (result.token) {
        //     // In this example, we're simply displaying the token
        //     successElement.querySelector('.token').textContent = result.token.id;
        //     successElement.classList.add('visible');
        //
        //     // In a real integration, you'd submit the form with the token to your backend server
        //     //var form = document.querySelector('form');
        //     //form.querySelector('input[name="token"]').setAttribute('value', result.token.id);
        //     //form.submit();
        //   } else if (result.error) {
        //     errorElement.textContent = result.error.message;
        //     errorElement.classList.add('visible');
        //   }
        // }

        let cardBrandToPfClass = {
          visa: 'pf-visa',
          mastercard: 'pf-mastercard',
          amex: 'pf-american-express',
          discover: 'pf-discover',
          diners: 'pf-diners',
          jcb: 'pf-jcb',
          unknown: 'pf-credit-card',
        }

        function setBrandIcon(brand) {
          let brandIconElement = document.getElementById('brand-icon')
          let pfClass = 'pf-credit-card'
          if (brand in cardBrandToPfClass) {
            pfClass = cardBrandToPfClass[brand]
          }
          for (let i = brandIconElement.classList.length - 1; i >= 0; i--) {
            brandIconElement.classList.remove(brandIconElement.classList[i])
          }
          brandIconElement.classList.add('pf')
          brandIconElement.classList.add(pfClass)
        }
        cardNum.on(
          'change',
          function(event) {
            // console.log(event.brand)
            if (['amex', 'discover', 'jcb', 'mastercard', 'unknown', 'visa'].includes(event.brand)) {
              this.cardBrand = event.brand
            } else {
              this.cardBrand = 'unknown'
            }
            let displayError = document.getElementById('card-number-error')
            if (event.error) {
              this.incorrectNumber = true
              this.focusedNum = false
              displayError.textContent = event.error.message
            } else {
              displayError.textContent = ''
              this.incorrectNumber = false
              if (event.complete) {
                this.cardExpiry.focus()
                this.focusedNum = false
              } else {
                this.focusedNum = true
              }
            }
          }.bind(this),
        )
      } catch (e) {
        console.error('mounting stripe elements error', e)
      }
    },
    async showPaymentMethod() {
      this.cardBrand = 'unknown'
      this.showPaymentMethodButton = true
      if (!this.alreadyShownButton) {
        await this.stripeCard()
      }
    },
    async focusNext(dst) {
      this.$refs[dst].focusIn()
    },
    async planSelected(plan) {
      // console.log('get plans', this.getPlans(this.typeOfPlan))
      this.plan = plan === this.getPlans(this.typeOfPlan)[0].value ? this.getPlans(this.typeOfPlan)[0].stripeName : this.getPlans(this.typeOfPlan)[1].stripeName

      let routeCopy = { ...this.$route }
      await this.$router.replace({
        path: routeCopy.path,
        name: routeCopy.name,
        component: this,
        query: {
          ...routeCopy.query,
          isMonthly: (this.plan == 'standardMonthly').toString(),
        },
        hash: routeCopy.hash,
      })

      this.duration = this.plan === this.getPlans(this.typeOfPlan)[1].stripeName ? this.$t('perMonth') : this.$t('perYear')
      if (this.loadedDetails) {
        await this.updateSubscriptionPriceDetails()
      }
    },
    async countrySelected() {
      // this.countryCode = this.country.code
      // console.log('COUNTRY SELECTED',this.country.name)
      this.countryCode = this.getCodeFromName(this.country.name)
      if (this.loadedDetails) {
        await this.updateSubscriptionPriceDetails()
      }
      // if(!this.cardLast4 )
      //   await this.focusNext("companyInputRef")
    },
    async businessPurchaseToggled() {
      this.businessPurchase = !this.businessPurchase
      if (this.loadedDetails) {
        await this.updateSubscriptionPriceDetails()
      }
    },
    async updateSubscriptionPriceDetails() {
      // console.log('updateSubscriptionPriceDetails ARGUMENTS', this.countryCode,
      // this.teamId, this.plan, navigator.language, this.businessPurchase)
      let res = await this.getSubscriptionPriceDetails(this.countryCode, this.teamId, this.plan, navigator.language, this.businessPurchase)
      // console.log('updateSubscriptionPriceDetails RESPONSE', res)

      if (res.status === 'ok') {
        this.activeUsers = res.activeMembersCount
        this.pricePerPersonPerMonth = res.pricePerMemberNoTaxFormatted
        // this.duration = '1 month'
        if (this.activeUsers === undefined) {
          this.activeUsers = 0
        }

        this.subtotal = res.priceTotalNoTaxFormatted

        if (res.invoiceLines && res.invoiceLines.length > 0) {
          let invoiceLineSubscription = res.invoiceLines.find(el => el.type == 'subscription')
          this.invoiceLineUnused = res.invoiceLines.find(el => el.type == 'unused-time')
          this.unused = this.invoiceLineUnused.priceFormatted
          this.subtotal = invoiceLineSubscription.priceFormatted
        }

        this.tax = res.taxTotalFormatted
        this.total = res.priceTotalWithTaxFormatted
        this.savePercent = res.yearlySavePercentage
      }
    },
    getNameFromCode(code) {
      for (let o of this.countries) {
        if (o.code === code) return o.name
      }
    },
    getCodeFromName(name) {
      for (let o of this.countries) {
        if (o.name === name) return o.code
      }
    },
    async getBacked() {
      await this.$router.push({
        path: '/',
        query: { teamId: this.teamId, tabName: 'TeamBilling' },
        hash: 'AdminSettings',
      })
    },
    async pushToDashboard() {
      await this.$router.push({
        path: '/',
        name: 'Dashboard',
        query: {
          teamId: this.teamId,
        },
      })
    },
    async paymentCompletedSucessfuly() {
      this.paybuttontext = this.$t('completed')
      this.loadingAnimation = false

      await this.sleep(2000)

      await this.getBacked()

      EventBus.$emit('new-info-bar-msg', {
        msg: this.$t('teamUpgradedTo') + ' ' + this.$t(this.plan) + '. ' + this.$t('thankYou'),
      })
      // this.$emit('close')
    },
    async processingPayment() {
      let res = await this.getPaymentStatus(this.teamId, this.plan)
      // console.log('res',res.subscriptionStatus,res.redirectUrl)
      if (res.status === 'ok') {
        if (res.redirectUrl) {
          window.clearInterval(this.refreshIntervalId)
          this.redirectUrl = res.redirectUrl
          this.refreshVerifyPopup = true
        } else {
          if (res.subscriptionStatus === 'active' && !res.pendingPlan) {
            window.clearInterval(this.refreshIntervalId)
            await this.paymentCompletedSucessfuly()
            EventBus.$emit('reload-teams')
            this.getTeamById(this.teamId).then(team => {
              this.$store.commit('setSelectedTeam', team)
            })
            this.showRedirectVerifyPopup = false
          } else if (res.subscriptionStatus != 'incomplete' && res.subscriptionStatus != 'active') {
            this.clicked = false
            this.showRedirectVerifyPopup = false
          }
        }
      } else {
        this.showServerError(res)
      }
    },
    checkIfCorrect() {
      let correct = true
      if (!this.cardLast4 && !this.isBillingInfo) {
        correct = this.$refs.companyInputRef.validate()
        correct = this.$refs.line1.validate() && correct
        correct = this.$refs.city.validate() && correct
        correct = this.$refs.zipCode.validate() && correct
        if (this.businessPurchase && this.isEU) correct = this.$refs.vatId.validate() && correct
      }
      if (!correct) {
        document.getElementById('companyInfo1').scrollIntoView({
          behavior: 'smooth',
        })
      }
      return correct
    },
    handlePaymentError(errorResp) {
      if (errorResp) {
        this.disablecondition = 0
        this.loadingAnimation = false

        if (this.showPaymentMethodButton || !this.cardLast4) {
          this.cardNumber.update({ disabled: false })
          this.cardExpiry.update({ disabled: false })
          this.cardCvc.update({ disabled: false })
        }
        this.paybuttontext = this.$t('paynow')
        this.showPaymentError(errorResp)
        this.clicked = false
        this.$store.commit('setShowProgressBar', false)
      }
    },

    async pay() {
      if (this.checkIfCorrect()) {
        if (this.showPaymentMethodButton || !this.cardLast4) {
          this.cardNumber.update({ disabled: true })
          this.cardExpiry.update({ disabled: true })
          this.cardCvc.update({ disabled: true })
        }
        this.paybuttontext = this.$t('processing')
        try {
          this.disablecondition = 1
          this.loadingAnimation = true
          this.clicked = true
          this.$store.commit('setShowProgressBar', true)

          if (this.showPaymentMethodButton || !this.isBillingInfo || !this.cardLast4) {
            this.paymentMethodId = null
            try {
              this.$refs.nameOnCard.validate()
              const paymentMethod = await this.createPaymentMethod('card', this.cardNumber, this.billingDetails)
              if (paymentMethod) {
                if (paymentMethod.error) {
                  this.handlePaymentError(paymentMethod)
                  return
                } else {
                  this.paymentMethodId = paymentMethod.id
                }
              } else {
                this.handlePaymentError(paymentMethod)
                return // DO NOT CONTINUE IF NOT PROPER PAYMENT METHOD
              }
            } catch (e) {
              this.handlePaymentError(e)
              return
            }
          }

          var vatToSend = this.isEU && this.businessPurchase ? this.vatId : ''
          let res
          if (!this.cardLast4 && !this.isBillingInfo) {
            if (this.isEU) {
              res = await this.createSubscriptionEU(
                this.companyName,
                this.countryCode,
                this.plan,
                this.businessPurchase,
                this.teamId,
                this.paymentMethodId,
                this.addressLine1,
                this.addressLine2,
                this.city,
                this.stateProvince,
                this.zipCode,
                vatToSend,
                this.currency,
              )
            } else {
              res = await this.createSubscriptionNonEU(
                this.companyName,
                this.countryCode,
                this.plan,
                this.businessPurchase,
                this.teamId,
                this.paymentMethodId,
                this.addressLine1,
                this.addressLine2,
                this.city,
                this.stateProvince,
                this.zipCode,
                this.currency,
              )
            }
          } else {
            res = await this.switchSubscription(
              this.plan,
              this.teamId,
              this.showPaymentMethodButton ? this.paymentMethodId : undefined, // use new or old payment method
              this.currency,
            )
          }

          if (res.status === 'ok' && this.paymentMethodId !== null) {
            // console.log('sub status', res.subscriptionStatus)
            if (res.subscriptionStatus == 'active' && !res.pendingPlan) {
              this.clicked = false
              await this.paymentCompletedSucessfuly()
              EventBus.$emit('reload-teams')
              this.getTeamById(this.teamId).then(team => {
                this.$store.commit('setSelectedTeam', team)
              })
              this.$store.commit('setShowProgressBar', false)
            } else if (res.subscriptionStatus == 'incomplete' || res.pendingPlan) {
              this.paybuttontext = this.$t('processing')
              this.showRedirectVerifyPopup = true
              this.refreshIntervalId = window.setInterval(() => {
                this.processingPayment()
              }, 4000)
            } else {
              // something went wrong - strange subscription status
              this.clicked = false
              this.$store.commit('setShowProgressBar', false)
            }
          } else {
            this.disablecondition = 0
            this.loadingAnimation = false
            if (this.showPaymentMethodButton || !this.cardLast4) {
              this.cardNumber.update({ disabled: false })
              this.cardExpiry.update({ disabled: false })
              this.cardCvc.update({ disabled: false })
            }
            this.paybuttontext = this.$t('paynow')
            let defaultMsg
            if (this.paymentMethodId === null) {
              defaultMsg = this.$t('providePaymentDetails')
            } else {
              defaultMsg = this.$t('paymentErrorContactSupport') + ' ' + this.$t('pleaseContactSupport')
            }
            this.showPaymentError(res, defaultMsg)

            this.clicked = false
            this.$store.commit('setShowProgressBar', false)
            // if(res.status === 'ok') {
            //   EventBus.$emit('new-info-bar-msg', {
            //     msg: this.$t('teamUpgradedTo') + ' '
            //         + this.plan + '. '
            //         + this.$t('thankYou')
            //   })
            // } else {
            //     EventBus.$emit('new-info-bar-msg', {
            //         msg: this.$t('provideInvoiceData'),
            //         error: true,
            //     })
            // }
          }

          //// console.log('CREATE SUBSCRIPTION RESPONSE', res)
        } catch (e) {
          console.error('stripe e', e)
          EventBus.$emit('new-info-bar-msg', {
            msg: this.$t('paymentErrorContactSupport') + ' ' + this.$t('pleaseContactSupport'),
            error: true,
          })
          this.paybuttontext = this.$t('paynow')
          this.$store.commit('setShowProgressBar', false)
        }
      } else {
        EventBus.$emit('new-info-bar-msg', {
          msg: this.$t('provideInvoiceData'),
          error: true,
        })
        this.paybuttontext = this.$t('paynow')
        this.$store.commit('setShowProgressBar', false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
//.space-filler {
//  height: 500px;
//  width: 100%;
//  position: relative;
//}
.stripe-card {
  width: 300px;
  border: 1px solid grey;
}
h1 {
  font-size: 48px;
  line-height: 54px;
  margin-top: 84px;
  margin-bottom: 19px;
  text-align: center;
  font-family: $bold;
  @include respond-below(xs) {
    font-size: 36px;
    line-height: 48px;
    font-family: $bold;
  }
}
h6 {
  font-size: 20px;
  line-height: 36px;
  @include font-rule($bold);
}
h5 {
  margin-top: 18px;
  @include font-rule('averta-regular');
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 72px;
  text-align: center;

  @include respond-below(xs) {
    margin-bottom: 48px;
  }
}

.stripe-card.complete {
  border-color: green;
}
.main-wrapper {
  //overflow-x: hidden;
  width: 100%;
  z-index: 1001;
  background-color: white;
  @include center-with-flexbox();

  div.logo {
    position: absolute;
    top: 20px;
    left: 20px;
    cursor: pointer;
    img {
      width: 120px;
    }
  }
  div.close {
    position: absolute;
    top: 20px;
    right: 20px;
    cursor: pointer;
    &:hover {
      opacity: 0.5;
    }
  }

  div.main-column {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .header {
      text-align: center;
      overflow-wrap: break-word;
    }

    .loading-dots {
      width: 100%;
      height: 50px;
      background-color: $blue;
      color: white;
      font-size: 16px;
      margin-top: 30px;
      font-family: $bold;
      border: 1px solid $blue;
      margin-bottom: 60px;
      //&:disabled{
      //  background-color: lightblue;
      //}
      //margin-bottom: 60px;
      position: relative;
      overflow: hidden;
    }
    .loading-dots:after {
      content: ' .';
      animation: dots 1s steps(5, end) infinite;
    }
    @keyframes dots {
      0%,
      20% {
        color: rgba(0, 0, 0, 0);
        text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
      }
      40% {
        color: white;
        text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
      }
      60% {
        text-shadow: 0.25em 0 0 white, 0.5em 0 0 rgba(0, 0, 0, 0);
      }
      80%,
      100% {
        text-shadow: 0.25em 0 0 white, 0.5em 0 0 white;
      }
    }
    .pay-with-stripe {
      width: 100%;
      height: 50px;
      background-color: $blue;
      color: white;
      font-size: 16px;
      margin-top: 30px;
      font-family: $bold;
      border: 1px solid $blue;
      //&:disabled{
      //  background-color: lightblue;
      //}
      //margin-bottom: 60px;
      position: relative;
      overflow: hidden;
      @include button-ripple-effect($dark-blue, $darkest-blue);
      @include button-hover($blue, white);

      &:focus {
        border-radius: 0px;
        outline: none;
      }
    }

    .two-columns {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      .single-column {
        display: flex;
        flex-grow: 0;
        flex-direction: column;
        &.left-column {
          margin-right: 100px;
          margin-top: 36px;
          width: 460px;
          .company-info-section {
            * {
              margin-bottom: 24px;
            }
            p.field-title {
              font-size: 12px;
              text-transform: uppercase;
              @include font-rule('averta-semibold');
            }
            div {
              width: 460px;
            }
            .row {
              display: flex;
              margin-bottom: 0px;
              flex-direction: row;

              div:first-of-type {
                margin-right: 20px;
              }

              @include respond-below(xs) {
                flex-direction: column;
              }
            }

            #line1 {
              margin-bottom: 12px;
            }
          }

          #zip-code {
            width: 220px;
            @include respond-below(xs) {
              width: 100%;
              margin-right: 0px;
            }
          }

          .radio-button {
            width: 10%;
            border: 0px;
            margin-top: 5px;
            margin-bottom: 5px;
          }
          .row {
            display: flex;
            flex-direction: row;
            div:first-of-type {
              margin-right: 20px;
            }
          }
          .orpaywithcard {
            width: 100%;
            height: 50px;
            text-align: center;
            align-items: center;
            margin-top: 30px;
          }

          .payment-method-section {
            margin-top: 36px;

            .apple-pay {
              width: 100%;
              height: 50px;
              background-color: $black;
              color: white;
              font-size: 24px;
              border-radius: 8px;
              &:disabled {
                background-color: darkgray;
              }
            }
            .card-expiration-error {
              color: red;
            }
            .payment-grid {
              h6 {
                grid-area: payment-title;
              }
              display: grid;
              grid-template-columns: 219px auto;
              grid-column-gap: 22px;
              grid-row-gap: 24px;
              grid-template-areas:
                'payment-title payment-title'
                'nameOnCard nameOnCard'
                'cardNumber cardNumber'
                'cardExpiry cardCvc';

              @include respond-below(xs) {
                grid-template-areas:
                  'payment-title payment-title'
                  'nameOnCard nameOnCard'
                  'cardNumber cardNumber'
                  'cardExpiry cardExpiry'
                  'cardCvc cardCvc';
              }
            }
          }

          .radio-group-subtitle {
            font-family: $regular;
            font-size: 16px;
            line-height: 24px;
            margin-bottom: 12px;
            margin-top: 48px;
          }

          #vat-id {
            margin-top: 24px;
          }
        }

        &.right-column {
          position: sticky;
          top: 20px;

          height: fit-content;
          //margin-left: auto;
          box-sizing: border-box;
          width: 382px;
          border: 1px solid $blue;
          border-radius: 13px;
          background-color: #ffffff;
          box-shadow: 0 15px 20px 0 rgba(0, 0, 0, 0.15);
          font-size: 16px;

          .first-row {
            padding: 40px 40px 0px 40px;
            .inside-higher-row {
              border-bottom: 1px solid #d3d6f9;
              div {
                margin-top: 24px;
              }
              .left-position {
                display: inline-block;
                width: 50%;
              }
              .right-position {
                display: inline-block;
                width: 50%;
                text-align: right;
              }
              .equation {
                padding-bottom: 19px;
              }
              :last-child {
                margin-bottom: 0px;
              }
            }
            .inside-lower-row {
              padding-top: 16px;
              padding-bottom: 4px;
              //do poprawienia
              div {
                margin-bottom: 12px;
                :last-child {
                  margin-bottom: 12px;
                }
              }
            }
            .multip {
              color: #9093c0;
              padding: 0 4px;
            }
            .left-position {
              display: inline-block;
              width: 50%;
            }
            .right-position {
              display: inline-block;
              width: 50%;
              text-align: right;
            }

            //div {
            //    &:first-of-type {
            //        margin-top: 19px;
            //        padding-top: 19px;
            //    }
            //    display: flex;
            //    flex-direction: row;
            //    justify-content: space-between;
            //}
          }
          .second-row {
            width: 100%;
            padding: 30px 40px 30px 40px;
            background-color: $bg-gray;
            border-radius: 0 0 13px 13px;

            h6 {
              margin: 0;
            }

            .left-position {
              width: 50%;
              display: inline-block;
            }
            .right-position {
              width: 50%;
              display: inline-block;
              text-align: right;
            }
          }
        }
      }

      @include respond-below(md) {
        flex-direction: column-reverse;
        justify-content: center;
        .single-column {
          &.left-column {
            margin-right: 0px;
            width: 100%;
          }
          &.right-column {
            width: 100%;
            margin-bottom: 36px;
            position: static;
          }
        }
      }
      @include respond-below(xs) {
        .single-column {
          &.left-column {
            width: 100%;
            .company-info-section div {
              width: 100%;
            }
          }
          &.right-column {
            width: 100%;
            margin-bottom: 36px;
          }
        }
      }
    }
    #nameOnCard {
      grid-area: nameOnCard;
    }
    .pay-with-stripe {
      width: 100%;
      height: 50px;
      background-color: $blue;
      color: white;
      font-size: 16px;
      margin-top: 30px;
      grid-area: pay-with-stripe;
      //&:disabled{
      //  background-color: lightblue;
      //}
      margin-bottom: 60px;
    }
    .stripe-card-with-error {
      .card-error {
        @include font-rule('averta-regular');
        font-size: 13px;
        color: $red;
        margin-top: 6px;
        letter-spacing: 0.33px;
        line-height: 18px;
      }

      &.card-number {
        grid-area: cardNumber;
      }

      &.card-expiry {
        grid-area: cardExpiry;
      }

      &.card-cvc {
        grid-area: cardCvc;
      }
      .stripe-card-element {
        @include animate-border-default(2px, 0);
        @include font-rule('averta-regular');

        &.focused {
          &:not(.incorrect) {
            @include animate-border-focused();
          }
        }

        .incorrect {
          border: 1px solid $red;
        }
        .disabled {
          background-color: $white-lilac;
        }
        .brand-cardnumber-positioning {
          display: grid;
          grid-template-columns: 30px auto;
          grid-template-rows: 24px;
          grid-template-areas: 'cardBrand cardNumber';

          #cardNumber {
            grid-area: cardNumber;
            align-self: center;
          }
          .brand {
            grid-area: cardBrand;
          }
        }

        .field-title {
          @include font-rule('averta-semibold');
          @include label;
          margin-right: 3px;
          margin-bottom: 5px;

          .cvv-question-mark {
            position: relative;
            display: inline-block;

            img {
              cursor: pointer;
              vertical-align: text-top;
              margin-left: 4px;
              &:hover {
                opacity: 0.6;
              }
            }

            .cvv-message-explanation {
              visibility: hidden;
              display: flex;
              width: 280px;
              margin-left: -140px;
              padding: 15px;
              border: 1px solid $flax-bloom;
              background-color: white;
              box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);
              text-transform: none;

              //position of the tooltip
              position: absolute;
              bottom: 140%;
              left: 50%;

              img {
                margin-left: 15px;
                &:hover {
                  opacity: 1;
                }
              }
            }
          }

          .cvv-question-mark:hover .cvv-message-explanation {
            visibility: visible;
          }
        }

        position: relative;
        display: flex;
        flex-direction: column;

        label {
          font-size: 14px;
          padding: 11px 10px;
          width: 100%;
          color: $black;
          border: 1px solid $flax-bloom;
          background-color: #ffffff;
          transition: border-color 0.2s;

          &.focused {
            border-color: $blue;
          }
        }
      }
    }

    .payment-footer {
      color: $bluebell;
      text-align: center;
      grid-area: footer-stripe;
      margin-bottom: 120px;
      .footer-info {
        img {
          padding-right: 6px;
          vertical-align: -5%;
        }
      }
      .footer-stripe-info {
        img {
          vertical-align: middle;
        }
      }
    }

    .switch-yearly {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;

      margin-right: 0px;

      h6 {
        margin-bottom: 24px;
      }

      .single-column {
        &.right-column {
          margin-bottom: 60px;
          width: 462px;
          position: static;

          @include respond-below(xs) {
            width: 100%;
          }
        }
      }

      .payment-method {
        max-width: 462px;
        width: 100%;

        .row {
          display: flex;
          flex-direction: row;
          margin-bottom: 24px;
          .display-card {
            width: 100%;
            .display-card-tittle {
              font-weight: normal;
              font-size: 12px;
              line-height: 16px;
              @include font-rule('averta-semibold');
              text-transform: uppercase;
              color: #081014;
              margin-right: 3px;
              margin-bottom: 5px;
            }
            .display-card-div {
              width: 100%;
              color: #9093c0;
              background-color: #f6f6fc;
              border: 1px solid #d3d6f9;
              font-size: 14px;
              padding: 10px;
              img {
                float: left;
                margin-right: 10px;
                margin-top: -2px;
              }
            }
          }
          div:first-of-type {
            margin-right: 20px;
          }
          #cardNumberPlaceHolder {
            width: 100%;
          }
          .clavo-button {
            width: 50%;
            align-self: flex-end;
            height: 40px;
          }
          @include respond-below(xs) {
            flex-direction: column;
            div:first-of-type {
              margin-right: 0;
            }
            .clavo-button {
              width: 100%;
              margin-top: 12px;
            }
          }
        }

        .hide {
          display: none;
        }
        .show {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-column-gap: 22px;
          grid-row-gap: 24px;
          grid-template-areas:
            'nameOnCard nameOnCard'
            'cardNumber cardNumber'
            'cardExpiry cardCvc'
            'cancel cancel';
          max-width: 462px;

          .cancel-link {
            grid-area: cancel;
            font-size: 14px;
            text-align: center;
          }

          @include respond-below(xs) {
            grid-template-areas:
              'nameOnCard nameOnCard'
              'cardNumber cardNumber'
              'cardExpiry cardExpiry'
              'cardCvc cardCvc'
              'cancel cancel';
          }
        }
        &.ButtonTurnOffGrid {
          display: inline;
        }
      }
    }

    @include respond-below(xs) {
      padding-right: 20px;
      padding-left: 20px;
    }
  }
}
</style>
